'use client';

import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { type BaseComponentProps } from '@bedrock/core/contentful';
import { Button } from '@bedrock/ui/button';
import { TextInput } from '@bedrock/ui/text-input';
import { Textarea } from '@bedrock/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ContentfulFormParsedContent } from 'lib/contentful/types';
import { analytics } from 'ui/analytics';
import { LazyWrap } from 'ui/common/lazy-wrap';
import { CheckboxField, FormField, FormLayout } from 'ui/components';
import ReCAPTCHA, { useRecaptcha } from 'ui/recaptcha-v3';
import { getFieldByType, getLabel } from 'ui/utils/form';
import { type ContactUsSchema, contactUsSchema } from './schema';
import { submitToMs365 } from './submit-to-ms-365';

const CONTACT_US_FORM_TYPE = 'Contact Us';

export function ContactUsForm({
  fields,
  dictionary,
}: Readonly<BaseComponentProps<ContentfulFormParsedContent>>) {
  const { recaptchaToken, recaptchaRef, onVerify, onExpire, resetRecaptcha } = useRecaptcha();

  const formFields = fields.fields;
  const firstNameField = getFieldByType(formFields, 'First Name');
  const lastNameField = getFieldByType(formFields, 'Last Name');
  const emailField = getFieldByType(formFields, 'Email');
  const messageField = getFieldByType(formFields, 'Message');
  const termsField = getFieldByType(formFields, 'Terms');

  const { register, handleSubmit, reset, formState } = useForm({
    resolver: zodResolver(contactUsSchema),
    mode: 'onBlur',
  });

  const [submitRequestStatus, setSubmitRequestStatus] = useState<'idle' | 'success' | 'error'>(
    'idle',
  );
  const [submitRequestError, setSubmitRequestError] = useState('');

  const onSuccess: SubmitHandler<ContactUsSchema> = async (data) => {
    try {
      const result = await submitToMs365(data, recaptchaToken ?? '');
      if (result.success) {
        setSubmitRequestStatus('success');
        analytics.formSubmit({ formType: CONTACT_US_FORM_TYPE });
        reset();
        resetRecaptcha();
      } else {
        setSubmitRequestStatus('error');
        setSubmitRequestError(result.error ?? '');
        analytics.formSubmit({ formType: CONTACT_US_FORM_TYPE });
      }
    } catch (error) {
      setSubmitRequestError(error instanceof Error ? error.message : 'Failed to send email');
      setSubmitRequestStatus('error');
      analytics.formSubmit({ formType: CONTACT_US_FORM_TYPE });
    }
  };

  const handleOverlayClose = () => {
    setSubmitRequestStatus('idle');
  };

  return (
    <FormLayout
      onOverlayClose={handleOverlayClose}
      onSubmit={handleSubmit(onSuccess)}
      requiredFieldNote={dictionary.requiredFieldNote}
      status={submitRequestStatus}
      overlayMessages={{
        closeLabel: dictionary.close,
        errorMessage: submitRequestError,
        errorTitle: dictionary.errorTitle,
        successTitle: dictionary.successTitle,
        successMessage:
          dictionary.contactSuccessMessage ||
          'Your message has been sent. We will get back to you soon.',
      }}
    >
      <div className="flex w-full flex-col gap-3">
        <div className="flex flex-col gap-3 md:flex-row">
          <FormField error={formState.errors.firstName} label={getLabel(firstNameField)}>
            <TextInput
              className="border-0 !outline-none !ring-offset-0"
              {...register('firstName')}
              isValid={!formState.errors.firstName}
              maxLength={50}
              placeholder={firstNameField?.placeholder}
            />
          </FormField>
          <FormField error={formState.errors.lastName} label={getLabel(lastNameField)}>
            <TextInput
              className="border-0 !outline-none !ring-offset-0"
              {...register('lastName')}
              isValid={!formState.errors.lastName}
              maxLength={50}
              placeholder={lastNameField?.placeholder}
            />
          </FormField>
        </div>
        <FormField error={formState.errors.email} label={getLabel(emailField)}>
          <TextInput
            className="border-0 !outline-none !ring-offset-0"
            {...register('email')}
            isValid={!formState.errors.email}
            maxLength={254}
            placeholder={emailField?.placeholder}
          />
        </FormField>
        <FormField error={formState.errors.message} label={getLabel(messageField)}>
          <Textarea
            {...register('message')}
            className="min-h-[200px] resize-y text-wrap border-0 !outline-none !ring-offset-0"
            isValid={!formState.errors.message}
            maxLength={2000}
            placeholder={messageField?.placeholder}
          />
        </FormField>
      </div>
      <CheckboxField
        error={formState.errors.terms}
        id="contact-terms"
        label={termsField?.label}
        register={register('terms')}
      />
      <LazyWrap>
        <ReCAPTCHA ref={recaptchaRef} onExpire={onExpire} onVerify={onVerify} />
      </LazyWrap>
      <Button
        className="w-full md:max-w-[159px]"
        disabled={formState.isSubmitting}
        size="md"
        variant="light"
      >
        {formState.isSubmitting
          ? (dictionary.submitting ?? 'Submitting...')
          : (dictionary.contactSubmit ?? 'Contact Us')}
      </Button>
    </FormLayout>
  );
}

export default ContactUsForm;
